import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import TokenIcon from './TokenIcon';

// Content management tab of the publisher dashboard: a paginated list of the
// publisher's registered content with inline edit (title, description, price,
// image) and an active/inactive toggle. "Active" means status === 'publish',
// which is what the discovery feed filters on; deactivating sets 'draft'.

const PAGE_SIZE = 10;

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const StatusPill = ({ active }) => (
  <span
    className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-bold tracking-wide ${
      active ? 'bg-wp-success/20 text-wp-success' : 'bg-wp-border text-wp-muted'
    }`}
  >
    {active ? 'ACTIVE' : 'INACTIVE'}
  </span>
);

const EditModal = ({ item, onClose, onSaved }) => {
  const [form, setForm] = useState({
    title: item.title || '',
    description: item.description || '',
    tokenCost: item.tokenCost,
    featuredImageUrl: item.featuredImageUrl || '',
  });
  const [error, setError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Escape closes the dialog. Clicking the backdrop already did, but that is
  // mouse-only — without this the modal is a keyboard trap for anyone who cannot
  // reach the Cancel button.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSave = async () => {
    setError('');
    const tokenCost = parseInt(form.tokenCost, 10);
    if (!Number.isInteger(tokenCost) || tokenCost < 1) {
      setError('Price must be a positive whole number of tokens.');
      return;
    }
    setIsSaving(true);
    try {
      const body = {
        title: form.title.trim() || null,
        description: form.description.trim() || null,
        tokenCost,
        featuredImageUrl: form.featuredImageUrl.trim() || null,
      };
      const response = await api.patch(
        `/publisher-dashboard/me/content/${encodeURIComponent(item.postId)}`,
        body
      );
      onSaved(response.data.content);
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.errors?.[0]?.message || 'Failed to save changes.');
      setIsSaving(false);
    }
  };

  const field = (label, node) => (
    <div>
      <label className="block text-sm font-medium text-wp-muted mb-1">{label}</label>
      {node}
    </div>
  );
  const inputClass =
    'block w-full bg-wp-bg text-white placeholder-wp-muted px-3 py-2 border border-wp-border rounded-lg focus:outline-none focus:ring-2 focus:ring-wp-accent';

  return (
    // role="presentation" on the backdrop: it is a click target for convenience,
    // not a control. Escape (above) is the keyboard equivalent, so the behaviour
    // is not mouse-only.
    <div
      role="presentation"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      // Close only when the backdrop itself is clicked. The previous version
      // stopped propagation on the dialog instead, which meant attaching a click
      // handler to a non-interactive role just to cancel an outer one.
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="wp-edit-content-title"
        className="w-full max-w-lg bg-wp-surface rounded-xl border border-wp-border p-6 space-y-4"
      >
        <div>
          <h3 id="wp-edit-content-title" className="text-white text-lg font-semibold">Edit content</h3>
          <p className="text-wp-muted text-xs mt-0.5 font-mono">{item.postId}</p>
        </div>

        {field('Title', <input type="text" value={form.title} onChange={set('title')} className={inputClass} maxLength={512} />)}
        {field(
          'Description',
          <textarea value={form.description} onChange={set('description')} rows={3} className={inputClass} maxLength={1024} />
        )}
        <div className="grid grid-cols-2 gap-4">
          {field('Price (tokens)', <input type="number" min="1" step="1" value={form.tokenCost} onChange={set('tokenCost')} className={inputClass} />)}
          {field('Featured image URL', <input type="url" value={form.featuredImageUrl} onChange={set('featuredImageUrl')} className={inputClass} placeholder="https://…" />)}
        </div>

        {error && <p className="text-wp-danger text-sm">{error}</p>}

        <div className="flex justify-end gap-3 pt-1">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium rounded-lg text-wp-muted hover:text-white transition-colors">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 text-sm font-bold rounded-lg bg-wp-accent text-wp-bg hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {isSaving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

const PublisherContentTab = () => {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefetching, setIsRefetching] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null);
  const [togglingId, setTogglingId] = useState(null);

  const totalPages = Math.max(Math.ceil(total / PAGE_SIZE), 1);

  const fetchPage = useCallback(async (pageNum, initial) => {
    if (!initial) setIsRefetching(true);
    setError('');
    try {
      const response = await api.get(`/publisher-dashboard/me/content?page=${pageNum}&limit=${PAGE_SIZE}`);
      setItems(response.data.items);
      setTotal(response.data.total);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load content.');
    } finally {
      setIsLoading(false);
      setIsRefetching(false);
    }
  }, []);

  useEffect(() => {
    fetchPage(page, page === 1 && items.length === 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, fetchPage]);

  const replaceItem = (updated) => {
    setItems((prev) => prev.map((it) => (it.postId === updated.postId ? updated : it)));
  };

  const toggleActive = async (item) => {
    const nextStatus = item.status === 'publish' ? 'draft' : 'publish';
    setTogglingId(item.postId);
    try {
      const response = await api.patch(`/publisher-dashboard/me/content/${encodeURIComponent(item.postId)}`, {
        status: nextStatus,
      });
      replaceItem(response.data.content);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update status.');
    } finally {
      setTogglingId(null);
    }
  };

  if (isLoading) {
    return <p className="text-center text-wp-muted py-16">Loading content…</p>;
  }

  return (
    <div className="space-y-4">
      {error && <p className="text-wp-danger text-sm">{error}</p>}

      {items.length === 0 && !error ? (
        <div className="bg-wp-surface rounded-xl border border-wp-border p-10 text-center space-y-2">
          <p className="text-white text-lg font-semibold">No content registered yet</p>
          <p className="text-wp-muted text-sm">
            Content appears here when your site registers it — via the WordPress plugin or the{' '}
            <a href="/docs" className="text-wp-accent hover:opacity-80">Partner API</a>.
          </p>
        </div>
      ) : (
        <div className={`bg-wp-surface rounded-xl border border-wp-border p-5 transition-opacity ${isRefetching ? 'opacity-60' : ''}`}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-wp-muted text-left">
                  <th className="py-2 pr-4 font-medium">Content</th>
                  <th className="py-2 pr-4 font-medium">Description</th>
                  <th className="py-2 pr-4 font-medium text-right">Price</th>
                  <th className="py-2 pr-4 font-medium">Status</th>
                  <th className="py-2 pr-4 font-medium whitespace-nowrap">Updated</th>
                  <th className="py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="text-white">
                {items.map((item) => {
                  const isActive = item.status === 'publish';
                  return (
                    <tr key={item.postId} className="border-t border-wp-border align-middle">
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-3 min-w-[220px]">
                          <div className="w-10 h-10 rounded-lg bg-wp-border overflow-hidden shrink-0 flex items-center justify-center text-lg">
                            {item.featuredImageUrl ? (
                              <img src={item.featuredImageUrl} alt="" className="w-full h-full object-cover" loading="lazy" />
                            ) : (
                              '📰'
                            )}
                          </div>
                          <div className="min-w-0">
                            {item.url ? (
                              <a href={item.url} target="_blank" rel="noopener noreferrer" className="block truncate max-w-[260px] hover:text-wp-accent font-medium">
                                {item.title || item.postId}
                              </a>
                            ) : (
                              <span className="block truncate max-w-[260px] font-medium">{item.title || item.postId}</span>
                            )}
                            <p className="text-wp-muted text-xs font-mono truncate">{item.postId}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 pr-4 text-wp-muted max-w-[280px]">
                        <span className="block truncate">{item.description || '—'}</span>
                      </td>
                      <td className="py-3 pr-4 text-right whitespace-nowrap" style={{ fontVariantNumeric: 'tabular-nums' }}>
                        <span className="inline-flex items-center gap-1">
                          <TokenIcon size={12} /> {item.tokenCost}
                        </span>
                      </td>
                      <td className="py-3 pr-4">
                        <StatusPill active={isActive} />
                      </td>
                      <td className="py-3 pr-4 text-wp-muted whitespace-nowrap">{fmtDate(item.updatedAt)}</td>
                      <td className="py-3 text-right whitespace-nowrap">
                        <button
                          onClick={() => setEditing(item)}
                          className="text-wp-accent hover:opacity-80 text-sm font-medium mr-4"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => toggleActive(item)}
                          disabled={togglingId === item.postId}
                          className={`text-sm font-medium disabled:opacity-50 ${
                            isActive ? 'text-wp-muted hover:text-white' : 'text-wp-success hover:opacity-80'
                          }`}
                        >
                          {togglingId === item.postId ? 'Working…' : isActive ? 'Deactivate' : 'Activate'}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between mt-4 text-sm">
            <p className="text-wp-muted">
              {total} item{total === 1 ? '' : 's'} · page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
                disabled={page <= 1 || isRefetching}
                className="px-3 py-1.5 rounded-lg bg-wp-bg text-wp-muted hover:text-white border border-wp-border transition-colors disabled:opacity-40"
              >
                ← Prev
              </button>
              <button
                onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                disabled={page >= totalPages || isRefetching}
                className="px-3 py-1.5 rounded-lg bg-wp-bg text-wp-muted hover:text-white border border-wp-border transition-colors disabled:opacity-40"
              >
                Next →
              </button>
            </div>
          </div>
        </div>
      )}

      <p className="text-wp-muted text-xs">
        Deactivated content is hidden from the WallPass discovery feed immediately. Readers who already unlocked it keep
        their access on your site.
      </p>

      {editing && (
        <EditModal
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            replaceItem(updated);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
};

export default PublisherContentTab;
